<template>
  <section
    v-if="Object.keys(props.data).length > 0"
    class="features base-section sm"
    :class="[ props.data.parentClasses, props.data.parentStyle ? props.data.parentStyle : 'defaultStyle' ]"
  >
    <div class="container col">
      <div class="row">
        <div
          v-if="props.data.specification?.list?.length"
          class="features-block inner-section col xs12 s12 m12 l12"
        >
          <strong class="label primary-color-text text-base-1-2">{{ $t('featuresSpecifications') }}</strong>
          <div class="row valign-wrapper">
            <div
              v-if="props.data.specification.image && props.data.specification.image.original"
              class="col s12 m12 l7 no-padding"
            >
              <div class="features-visual">
                <NuxtPicture
                  :img-attrs="{class: 'responsive-img', alt: props.data.specification.image.text}"
                  :src="props.data.specification.image.original"
                  :placeholder="[100, 60, 40, 20]"
                />

                <ol class="ordered-list numbers-list">
                  <template v-for="item, i in props.data.specification.list">
                    <li
                      v-if="!item.skipMarker"
                      :key="item"
                      :class="{active: currentItem ? currentItem === formatIndex(i) * 1 : formatIndex(i) * 1 === 1}"
                      style="position: absolute;"
                      :style="{
                        left: item.xPercent + '%',
                        top: item.yPercent + '%'
                      }"
                      @mouseover="currentItem = formatIndex(i) * 1"
                    ></li>
                  </template>
                </ol>
              </div>
            </div>
            <div class="features-content col s12 m12 l5">
              <div class="ordered-list diagram-carousel">
                <Swiper
                  :modules="[Pagination, Navigation]"
                  :slides-per-view="1"
                  :loop="false"
                  :space-between="0"
                  :navigation="{
                    prevEl: '.features__navigation__prev',
                    nextEl: '.features__navigation__next'
                  }"
                  :pagination="{
                    clickable: true
                  }"
                  @slide-change="onSlideChange"
                  @swiper="onSwiper"
                >
                  <SwiperSlide
                    v-for="item, i in props.data.specification.list"
                    :key="item"
                  >
                    <div
                      v-if="!item.skipMarker"
                      :key="item"
                      :class="{active: currentItem ? currentItem === formatIndex(i) * 1 : formatIndex(i) * 1 === 1}"
                      class="order-list-item"
                      @mouseover="currentItem = formatIndex(i) * 1"
                    >
                      <div class="descr">
                        <div class="h4 bold bold">
                          {{ item.name }}
                        </div>
                        <p>{{ item.desc }}</p>
                      </div>
                    </div>
                  </SwiperSlide>

                  <div class="slider-nav features__navigation">
                    <button
                      class="features__navigation__prev swiper-arrow prev"
                      :aria-label="$t('preview')"
                      type="button"
                    >
                      <span class="icon icon-left-arrow"></span>
                    </button>
                    <button
                      class="features__navigation__next swiper-arrow next"
                      :aria-label="$t('next')"
                      type="button"
                    >
                      <span class="icon icon-right-arrow"></span>
                    </button>
                  </div>
                </swiper>
              </div>
            </div>
          </div>
        </div>

        <div class="description-block  col xs12 s12 m12 l8 mb-0">
          <div class="row">
            <template v-if="props.data.showDimensions && (props.data.dimensions && props.data.dimensions.list.length != 0 || props.data.printAreas && props.data.printAreas.list.length != 0 || props.data.dimensions.image && props.data.dimensions.image.original)">
              <div
                v-if="props.data.dimensions.image && props.data.dimensions.image.original"
                class="img-holder col s12 m6 flex"
              >
                <NuxtPicture
                  loading="lazy"
                  :img-attrs="{class: 'responsive-img', alt: props.data.dimensions.image.text}"
                  :src="props.data.dimensions.image.original"
                  height="400"
                  width="400"
                />
              </div>
              <div class="description-text col xs12 m6">
                <div
                  v-if="props.data.dimensions && props.data.dimensions.list.length != 0 || props.data.printAreas && props.data.printAreas.list.length != 0"
                  class="h4 bold"
                >
                  {{ $t('dimensions') }}
                </div>
                <dl class="dimension-list">
                  <template v-if="props.data.dimensions">
                    <template v-for="item, i in props.data.dimensions.list">
                      <dt
                        v-if="item"
                        :key="i"
                        class="size-name size-name--wide-lt"
                      >
                        {{ item[0] }}
                      </dt>
                      <dd
                        v-if="item"
                        :key="i"
                        class="size-spec"
                      >
                        <span class="imperial">{{ item[1] }}</span>
                        <i class="dot">•</i> <span class="metric">{{ item[2] }}</span>
                      </dd>
                    </template>
                  </template>

                  <template v-if="(props.data.printAreas.list ?? {}).length != 0">
                    <template v-for="item in props.data.printAreas.list">
                      <dt
                        v-if="item"
                        :key="item"
                        class="size-name size-name--wide-lt"
                      >
                        {{ item.name }}
                      </dt>
                      <dd
                        v-if="item"
                        :key="item"
                        class="size-spec"
                      >
                        <span class="imperial">{{ item.dimentionIn }}</span>
                      </dd>
                    </template>
                  </template>
                </dl>
              </div>
            </template>
          </div>
        </div>

        <div
          class="description-block row col l4 mb-0 flex-column hide-on-med-and-down"
        >
          <div class="description-text">
            <span
              v-if="props.data.parentStyle === 'secondStyle'"
              class="label"
            >
              {{ $t('widgets.features.label') }}
            </span>

            <div
              v-else
              class="h4 bold"
            >
              {{ $t('widgets.features.label') }}
            </div>

            <ul class="check-list">
              <li
                v-html="$t('widgets.features.item1')"
              ></li>
              <li
                v-html="$t('widgets.features.item2')"
              ></li>
              <li
                v-html="$t('widgets.features.item3')"
              ></li>
              <li
                v-html="$t('widgets.features.item4')"
              ></li>
            </ul>
          </div>
        </div>

        <template v-if="props.data.showCertifications && props.data.certificates">
          <div
            v-if="props.data.certificates.list.length"
            class="product-option-holder row col s12 m5 offset-m7 l4 offset-l8 mb-0 mt-4"
          >
            <div class="product-option-block">
              <div class="h4 bold">
                {{ $t('certifications') }}
              </div>
              <ul class="custom-radio-list compliance">
                <li
                  v-for="item, i in props.data.certificates.list"
                  :key="i"
                  class="tool-tip-wrapper"
                >
                  <HelpersTooltipHelpers
                    :data="{
                      parentClasses: 'compliance',
                      tooltip: `<div><div class='text-[18px] bold'>${item.name}</div><div>${item.description}</div></div>`,
                      content: `<i
                        class='compliance-info ${item.icon_name}'
                        title='${item.name}'
                      ></i>`
                    }"
                  />
                </li>
              </ul>
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Pagination, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'

const featuresSlider = ref(null)
const currentItem = ref(null)

/**
* Props
*/
const props = defineProps({
  data: {
    type: [Object],
    default: null
  },
  productData: {
    type: [Object],
    default: null
  }
})

/**
* Swiper actions
*/
const onSwiper = (swiper) => {
  featuresSlider.value = swiper
}

const onSlideChange = (index) => {
  currentItem.value = index.activeIndex + 1
}

const formatIndex = (index) => {
  return index + 1
}

/**
* If update current slide
*/
watch(currentItem, (newCurrentItem) => {
  if (currentItem !== newCurrentItem && window.innerWidth < 991) {
    featuresSlider.value.slideTo(newCurrentItem - 1)
  }
}, { deep: true })
</script>

<style lang="scss" scoped>
/*Compliance Icons*/
/*Compliance Icons*/
@font-face {
  font-family: "CustomUSB Compliance";
  font-style: normal;
  font-weight: normal;

  font-display: block;
  src: url("~/assets/customusb/fonts/customusb-compliance/compliance.woff") format("woff");
}

.features {
  position: relative;
  &:deep(.compliance) {
    [class^="compliance-"],
    [class*="compliance-"] {
      display: none;
      font-family: initial;
      font-style: normal;
      font-variant: normal;
      font-weight: normal;
      text-transform: none;

      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      speak: none;
    }

    [class^="compliance-"],
    [class*="compliance-"] {
      display: inherit;
      font-family: "CustomUSB Compliance" !important;
    }

    /* mmc memory speed ratings */
    .compliance-sd {
      &::before {
        content: "\e906";
      }
    }
    .compliance-sdhc {
      &::before {
        content: "\e907";
      }
    }
    .compliance-sdxc {
      &::before {
        content: "\e908";
      }
    }
    .compliance-micro-sd {
      &::before {
        content: "\e909";
      }
    }
    .compliance-micro-sdhc {
      &::before {
        content: "\e90a";
      }
    }
    .compliance-micro-sdxc {
      &::before {
        content: "\e90c";
      }
    }
    .compliance-speed-class-2 {
      &::before {
        content: "\e90f";
      }
    }
    .compliance-speed-class-4 {
      &::before {
        content: "\e910";
      }
    }
    .compliance-speed-class-6 {
      &::before {
        content: "\e911";
      }
    }
    .compliance-speed-class-10 {
      &::before {
        content: "\e912";
      }
    }
    .compliance-uhs-speed-class-1 {
      &::before {
        content: "\e916";
      }
    }
    .compliance-uhs-speed-class-3 {
      &::before {
        content: "\e917";
      }
    }
    .compliance-video-speed-class-6 {
      &::before {
        content: "\e918";
      }
    }
    .compliance-video-speed-class-10 {
      &::before {
        content: "\e919";
      }
    }
    .compliance-video-speed-class-30 {
      &::before {
        content: "\e91a";
      }
    }
    .compliance-video-speed-class-60 {
      &::before {
        content: "\e91b";
      }
    }
    .compliance-video-speed-class-90 {
      &::before {
        content: "\e91c";
      }
    }

    /* compliance */
    .compliance-ce {
      &::before {
        content: "\e900";
      }
    }
    .compliance-fcc {
      &::before {
        content: "\e901";
      }
    }
    .compliance-ul {
      &::before {
        content: "\e90b";
      }
    }
    .compliance-rohs {
      &::before {
        content: "\e903";
      }
    }
    .compliance-reach {
      &::before {
        content: "\e90d";
      }
    }
    .compliance-prop-65 {
      &::before {
        content: "\e914";
      }
    }
    .compliance-prop-65-ca {
      &::before {
        content: "\e915";
      }
    }
    .compliance-ipx4 {
      &::before {
        content: "\e913";
      }
    }
    .compliance-ipx7 {
      &::before {
        content: "\e905";
      }
    }
    .compliance-recycle {
      &::before {
        content: "\e902";
      }
    }
    .compliance-do-not-trash {
      &::before {
        content: "\e90e";
      }
    }
    .compliance-green-dot {
      &::before {
        content: "\e904";
      }
    }
  }

  &-block {
    &.inner-section {
      @media only screen and (min-width: 601px) {
        margin-bottom: 1rem;
      }
    }
  }
  .features-visual {
    position: relative;
    &:deep(img) {
      width: 100%;
    }
  }

  .description-block {
    position: relative;
    img {
      position: absolute;
      z-index: 0;
      max-height: 100%;
      @media only screen and (max-width: 600px) {
        position: relative;
        width: 100%;
      }
    }
  }
  .description-text {
    position: relative;
    z-index: 1;
  }
  .product-option-block {
    [class^="compliance-"],
    [class*="compliance-"] {
      display: inherit;
      font-family: "CustomUSB Compliance" !important;
    }
    &:deep(.compliance) {
      display: flex;
      margin: 0;
      li {
        padding: 0 5px !important;
        margin-bottom: 10px;
      }
      i {
        margin-bottom: 0;
        color: #93a2b3;
        font-size: 2.25rem;
        line-height: 1;
      }
    }
  }

  &__navigation {
    @media only screen and (min-width: 991px) {
      display: none;
    }
    .swiper-arrow {
      position: absolute;
      bottom: 0;
      display: flex;
      align-items: center;
      padding: 0;
      color: #015287;
      background: transparent;
      box-shadow: none;
      border: none;
      &:hover {
        color: color("primary-color", "base");
      }
      &.prev {
        left: 0;
        justify-content: left;
      }
      &.next {
        right: 0;
        justify-content: right;
      }
    }
  }

  .ordered-list {
    padding: 0;
    counter-reset: list;
    line-height: 1.5;

    &.numbers-list {
      li {
        transform: translate(-25px, -25px);
      }
    }

    .order-list-item,
    li {
      display: flex;
      flex-wrap: wrap;
      cursor: pointer;

      .descr {
        flex: 1 1 calc(100% - 61px);
      }

      &:not(.skip) {
        display: flex;
        cursor: pointer;
      }

      &.active {
        &::before {
          color: #fff;
          background-color: rgba(color("primary-color", "base"), .9);
        }
      }

      &::before {
        content: counter(list);
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 auto;
        width: 45px;
        height: 45px;
        margin-right: 10px;
        color: rgba(color("primary-color", "base"), .9);
        counter-increment: list;
        font-size: 1rem;
        font-weight: 500;
        line-height: 2;
        background-color: rgba(color("grey", "lighten-3"), .9);
        border: 3px solid #fff;
        border-radius: 50%;
        @media only screen and (max-width: 991px) {
          width: 32px;
          height: 32px;
        }
      }
      @media only screen and (max-width: 991px) {
        padding: 0 3rem;
        text-align: center;
      }
    }
  }

  .features-content {
    .order-list-item,
    li {
      &::before {
        @media only screen and (max-width: 991px) {
          display: none;
        }
      }
    }
    @media only screen and (min-width: 991px) {
      &:deep(.swiper-wrapper) {
        display: flex;
        flex-direction: column;
        .swiper-slide {
          width: 100%!important;
        }
      }
    }
    @media only screen and (max-width: 991px) {
      flex-direction: column;
    }
  }
}
</style>
